import i18n from '@/i18n';
/**
 * 工作流图片步骤与工作室主链对齐：funding_task + serverWorker + submit / 等待。
 *
 * 裁剪走 submitImageCropTask（Qwen Worker），超分/裂变走 taskAPI.submit
 * （FundingBatchJobService）。失败形态、重试、计费与对应 Studio 页同一条路。
 */
import { taskAPI } from '@/utils/api';
import {
  IMAGE_CROP_WORKER_MODE,
  pickCroppedUrlFromRow,
  pollImageCropBatch,
  submitImageCropTask,
} from '@/image-crop/studioSubmit';
import { resolveImg2ImgReferenceForApi } from './img2imgPublicUrl';
import { sleepVisibilityAware } from '@/utils/pageVisibility';
import {
  persistProcessedImageToSpace,
} from './sourcingWorkflowSpace';
import {
  extractUrlsFromFundingTask,
  waitForFundingTaskComplete,
} from './waitForFundingTask';
import {
  beginWorkflowFeatureTask,
  completeWorkflowFeatureImageTask,
  failWorkflowFeatureTask,
} from './workflowFeatureTask';
import { buildPipelineReferenceResult } from '@/pages/ipe/workbench/constants/fundingTaskImageUrlFields';

function throwIfAborted(shouldAbort) {
  if (typeof shouldAbort === 'function' && shouldAbort()) {
    const err = new Error('任务已终止');
    err.name = 'WorkflowTaskAbortedError';
    throw err;
  }
}

export async function resolveStudioWorkerImageUrl(currentUrl) {
  const resolved = await resolveImg2ImgReferenceForApi(currentUrl);
  if (!resolved) {
    throw new Error(i18n.t('workbench:ipeUtilError.pipelineImageUnresolved'));
  }
  return resolved;
}

async function persistWorkflowOutputImages(row, navId, urls, labelPrefix) {
  const canonicalUrls = [];
  let primarySpaceId = null;
  let primaryCanonicalUrl = '';
  for (let i = 0; i < urls.length; i += 1) {
    const saved = await persistProcessedImageToSpace(
      `wf-${navId}-${row.id}-${i}`,
      urls[i],
      `${labelPrefix}-${i + 1}`,
      { navId },
    );
    canonicalUrls.push(saved.imageUrl);
    if (i === 0) {
      primarySpaceId = saved.spaceId;
      primaryCanonicalUrl = saved.imageUrl;
    }
  }
  return { primarySpaceId, primaryCanonicalUrl, canonicalUrls };
}

function assertFundingTaskOk(task, taskName) {
  const status = String(task?.status || '').toLowerCase();
  if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
    const hint = task?.result?.failureHint || task?.result?.error;
    throw new Error(typeof hint === 'string' && hint.trim() ? hint.trim() : i18n.t('workbench:ipeUtilError.taskFailedNamed', { name: taskName }));
  }
}

async function extractCropOutputUrls(taskId, imageId) {
  const id = String(imageId || '').trim();
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const batch = await pollImageCropBatch(taskId, 1);
    const rows = Array.isArray(batch?.images) ? batch.images : [];
    const matched = id
      ? rows.find((row) => String(row?.imageId || row?.id || '') === id)
      : null;
    const row = matched || rows[0];
    const url = pickCroppedUrlFromRow(row);
    if (url) return [url];
    if (row && String(row.status || '').toLowerCase() === 'failed') {
      const msg = row.error || row.message;
      throw new Error(typeof msg === 'string' && msg.trim() ? msg.trim() : i18n.t('workbench:ipeUtilError.imageCropFailed'));
    }
    await sleepVisibilityAware(400);
  }
  return [];
}

async function extractStepOutputUrls(navId, task, taskId, imageId) {
  const fromTask = extractUrlsFromFundingTask(task);
  if (fromTask.length > 0) return fromTask;
  if (navId === 'imageCrop') {
    return extractCropOutputUrls(taskId || task?.id || task?.taskId, imageId);
  }
  return [];
}

async function submitStudioWorker(navId, taskId, imageId, imageUrl) {
  if (navId === 'imageCrop') {
    await submitImageCropTask({
      taskId,
      images: [{ imageId, imageUrl }],
      mode: IMAGE_CROP_WORKER_MODE,
    });
    return;
  }
  await taskAPI.submit(taskId);
}

/**
 * @param {'imageCrop' | 'imageEnhance' | 'imageFission'} navId
 */
export async function runStudioWorkerWorkflowStep({
  navId,
  taskType,
  taskName,
  row,
  currentSpaceId,
  currentUrl,
  workflowContext = {},
  shouldAbort,
  estimatedTotal = 1,
  initialResult = {},
  persistLabel,
} = {}) {
  throwIfAborted(shouldAbort);
  const imageUrl = await resolveStudioWorkerImageUrl(currentUrl);
  throwIfAborted(shouldAbort);
  const imageId = String(currentSpaceId || row?.id || `${navId}-wf`).trim();
  const previewUrl = currentUrl || imageUrl;
  const { taskId, initialResult: mergedResult } = await beginWorkflowFeatureTask({
    taskType: taskType || navId,
    taskName,
    imageIds: [imageId],
    previewUrl,
    estimatedTotal,
    workflowContext,
    initialResult: {
      ...buildPipelineReferenceResult({
        imageUrl,
        imageId,
        previewUrl,
      }),
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
      ...initialResult,
    },
  });

  try {
    throwIfAborted(shouldAbort);
    await submitStudioWorker(navId, taskId, imageId, imageUrl);
    throwIfAborted(shouldAbort);
    const task = await waitForFundingTaskComplete(taskId);
    assertFundingTaskOk(task, taskName);
    const urls = await extractStepOutputUrls(navId, task, taskId, imageId);
    if (urls.length === 0) {
      throw new Error(i18n.t('workbench:ipeUtilError.taskDoneNoImage', { name: taskName }));
    }
    const persisted = await persistWorkflowOutputImages(
      row,
      navId,
      urls,
      persistLabel || `工作流-${taskName}`,
    );
    const finalResult = await completeWorkflowFeatureImageTask(
      taskId,
      persisted.canonicalUrls,
      estimatedTotal,
      mergedResult,
      {
        outputSpaceId: persisted.primarySpaceId,
        outputImageUrl: persisted.primaryCanonicalUrl,
        engine: task?.result?.engine || mergedResult.engine,
        note: `插件工作流：与工作室主链同一 Worker（${navId}）`,
      },
      task?.result || {},
      navId,
    );
    return {
      taskId,
      spaceId: persisted.primarySpaceId,
      imageUrl: persisted.primaryCanonicalUrl,
      outputUrls: persisted.canonicalUrls,
      compositeImageUrls: persisted.canonicalUrls,
      result: finalResult,
    };
  } catch (error) {
    await failWorkflowFeatureTask(taskId, mergedResult, estimatedTotal, error);
    throw error;
  }
}
